import { useEffect, useState, useCallback } from 'react';

/**
 * Accessibility preferences (WCAG 2.1 AA supporting).
 * Feature-parity with the CERT.GOV.AZ widget (minus Reading Guide + Language).
 * All modes are applied to <html data-a11y-*="..."> and consumed by CSS.
 */

const STORAGE_KEY = 'idk_a11y_prefs_v3';

const DEFAULT_PREFS = {
  fontPercent: 100,          // 90 | 100 | 110 | 120 | 130 | 140 | 150
  colorMode: 'default',      // 'default' | 'dark' | 'light' | 'high'
  saturation: 'default',     // 'default' | 'high' | 'low' | 'mono'
  highlightTitles: false,
  highlightLinks: false,
  dyslexiaFont: false,
  letterSpacing: false,
  lineHeight: false,
  boldFont: false,
  bigCursor: false,
  stopAnimations: false,
};

const readPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PREFS;
  }
};

const applyToDocument = (p) => {
  const el = document.documentElement;
  el.dataset.a11yFontPercent = String(p.fontPercent);
  el.dataset.a11yColor = p.colorMode;
  el.dataset.a11ySaturation = p.saturation;
  el.dataset.a11yTitles = p.highlightTitles ? 'on' : 'off';
  el.dataset.a11yLinks = p.highlightLinks ? 'on' : 'off';
  el.dataset.a11yDyslexia = p.dyslexiaFont ? 'on' : 'off';
  el.dataset.a11yLetter = p.letterSpacing ? 'on' : 'off';
  el.dataset.a11yLine = p.lineHeight ? 'on' : 'off';
  el.dataset.a11yBold = p.boldFont ? 'on' : 'off';
  el.dataset.a11yCursor = p.bigCursor ? 'big' : 'default';
  el.dataset.a11yMotion = p.stopAnimations ? 'stopped' : 'normal';
};

export const useAccessibility = () => {
  const [prefs, setPrefs] = useState(readPrefs);

  useEffect(() => {
    applyToDocument(prefs);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (err) {
      // localStorage disabled/quota exceeded — non-fatal; log in dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[a11y] Could not persist preferences:', err);
      }
    }
  }, [prefs]);

  const update = useCallback((patch) => setPrefs((prev) => ({ ...prev, ...patch })), []);
  const adjustFont = useCallback((delta) =>
    setPrefs((prev) => ({ ...prev, fontPercent: Math.max(90, Math.min(150, prev.fontPercent + delta)) })), []);
  const reset = useCallback(() => setPrefs(DEFAULT_PREFS), []);

  const isDefault = JSON.stringify(prefs) === JSON.stringify(DEFAULT_PREFS);

  return { prefs, update, adjustFont, reset, isDefault };
};
